import {
  type CompiledEntityTemplate,
  type CompiledTemplateJson,
  FRAMEWORK_TEMPLATE_CONTRACT_VERSION,
} from "@repo/shared";
import type { JSONContent } from "@tiptap/react";
import { createDocumentTabMapFromCompiledTemplate } from "./schema-factory";
import { createTabInitialContent, extractTabFormData } from "./tab-registry";
import type {
  DocumentEntityType,
  DocumentSchemaConfig,
  DocumentTabMap,
  ExtractedFormData,
} from "./types";

export interface CompiledSchemaRegistry {
  byTabKey: Record<string, DocumentSchemaConfig>;
  byFrameworkEntityTypeKey: Record<string, DocumentTabMap>;
  templateVersionByFrameworkEntityTypeKey: Record<string, number>;
}

export function buildCompiledSchemaRegistry(
  compiledTemplateJson?: CompiledTemplateJson | null
): CompiledSchemaRegistry | null {
  if (!compiledTemplateJson) return null;

  if (compiledTemplateJson.contractVersion !== FRAMEWORK_TEMPLATE_CONTRACT_VERSION) {
    throw new Error(
      `Unsupported compiled template contractVersion '${String(compiledTemplateJson.contractVersion)}'. Expected ${FRAMEWORK_TEMPLATE_CONTRACT_VERSION}.`
    );
  }

  const byTabKey: Record<string, DocumentSchemaConfig> = {};
  const byFrameworkEntityTypeKey: Record<string, DocumentTabMap> = {};
  const templateVersionByFrameworkEntityTypeKey: Record<string, number> = {};

  for (const [frameworkEntityTypeKey, template] of Object.entries(
    compiledTemplateJson.templatesByEntityType
  )) {
    validateCompiledEntityTemplate(frameworkEntityTypeKey, template);

    const tabMap = createDocumentTabMapFromCompiledTemplate({
      entityType: frameworkEntityTypeKey,
      displayName: frameworkEntityTypeKey,
      template,
    });

    for (const [tabKey, schema] of Object.entries(tabMap)) {
      byTabKey[tabKey] = schema;
    }

    byFrameworkEntityTypeKey[frameworkEntityTypeKey] = tabMap;
    templateVersionByFrameworkEntityTypeKey[frameworkEntityTypeKey] = template.templateVersion;
  }

  return {
    byTabKey,
    byFrameworkEntityTypeKey,
    templateVersionByFrameworkEntityTypeKey,
  };
}

export function resolveRuntimeTabSchema(
  tabKey: string,
  input?: {
    compiledSchemaRegistry?: CompiledSchemaRegistry | null;
  }
): DocumentSchemaConfig {
  const registry = input?.compiledSchemaRegistry;
  if (!registry) {
    throw new Error(`Missing compiled schema registry while resolving tab '${tabKey}'.`);
  }

  const runtimeSchema = registry.byTabKey[tabKey];
  if (!runtimeSchema) {
    throw new Error(`Missing compiled runtime tab schema for '${tabKey}'.`);
  }

  return runtimeSchema;
}

export function getRuntimeTabSchemaIfPresent(
  tabKey: string,
  input?: {
    compiledSchemaRegistry?: CompiledSchemaRegistry | null;
  }
): DocumentSchemaConfig | null {
  return input?.compiledSchemaRegistry?.byTabKey[tabKey] ?? null;
}

export function resolveRuntimeDocumentSchema(input: {
  entityType: DocumentEntityType;
  tabKey: string;
  compiledSchemaRegistry?: CompiledSchemaRegistry | null;
}): DocumentSchemaConfig {
  const schema = resolveRuntimeTabSchema(input.tabKey, {
    compiledSchemaRegistry: input.compiledSchemaRegistry,
  });
  return schema;
}

export function createRuntimeDocumentInitialContent(input: {
  entityType: DocumentEntityType;
  existingData?: Record<string, unknown>;
  tabKey: string;
  compiledSchemaRegistry?: CompiledSchemaRegistry | null;
}): JSONContent {
  return createTabInitialContent(resolveRuntimeDocumentSchema(input), input.existingData);
}

export function extractRuntimeDocumentFormData(input: {
  entityType: DocumentEntityType;
  json: JSONContent;
  tabKey: string;
  compiledSchemaRegistry?: CompiledSchemaRegistry | null;
}): ExtractedFormData {
  return extractTabFormData(resolveRuntimeDocumentSchema(input), input.json);
}

export function resolveRuntimeEntityTabMap(
  entityType: DocumentEntityType,
  input?: {
    frameworkEntityTypeKey?: string | null;
    compiledSchemaRegistry?: CompiledSchemaRegistry | null;
  }
): DocumentTabMap {
  const frameworkEntityTypeKey = input?.frameworkEntityTypeKey ?? null;
  if (!frameworkEntityTypeKey) {
    throw new Error(
      `Missing framework entity type key while resolving tab map for '${entityType}'.`
    );
  }

  const registry = input?.compiledSchemaRegistry;
  if (!registry) {
    throw new Error(
      `Missing compiled schema registry while resolving tab map for '${frameworkEntityTypeKey}'.`
    );
  }

  const runtimeMap = registry.byFrameworkEntityTypeKey[frameworkEntityTypeKey];
  if (!runtimeMap) {
    throw new Error(
      `Missing compiled runtime tab map for framework entity '${frameworkEntityTypeKey}'.`
    );
  }

  return runtimeMap;
}

export function resolveRuntimeTemplateVersion(input: {
  frameworkEntityTypeKey: string;
  compiledSchemaRegistry?: CompiledSchemaRegistry | null;
}): number {
  const registry = input.compiledSchemaRegistry;
  if (!registry) {
    throw new Error(
      `Missing compiled schema registry while resolving template version for '${input.frameworkEntityTypeKey}'.`
    );
  }

  const runtimeVersion =
    registry.templateVersionByFrameworkEntityTypeKey[input.frameworkEntityTypeKey];
  if (typeof runtimeVersion === "number" && Number.isFinite(runtimeVersion) && runtimeVersion > 0) {
    return runtimeVersion;
  }

  throw new Error(
    `Missing compiled template version for framework entity '${input.frameworkEntityTypeKey}'.`
  );
}

function validateCompiledEntityTemplate(
  frameworkEntityTypeKey: string,
  template: CompiledEntityTemplate
): void {
  if (
    typeof template.templateVersion !== "number" ||
    !Number.isFinite(template.templateVersion) ||
    template.templateVersion <= 0
  ) {
    throw new Error(
      `compiledTemplateJson.templatesByEntityType.${frameworkEntityTypeKey}.templateVersion must be a positive number.`
    );
  }

  if (!Array.isArray(template.tabs)) {
    throw new Error(
      `compiledTemplateJson.templatesByEntityType.${frameworkEntityTypeKey}.tabs must be an array.`
    );
  }

  for (const tab of template.tabs) {
    if (typeof tab.tabKey !== "string" || tab.tabKey.trim().length === 0) {
      throw new Error(
        `compiledTemplateJson.templatesByEntityType.${frameworkEntityTypeKey}.tabs contains a tab with invalid tabKey.`
      );
    }

    if (typeof tab.contentExpression !== "string" || tab.contentExpression.trim().length === 0) {
      throw new Error(
        `compiledTemplateJson.templatesByEntityType.${frameworkEntityTypeKey}.tabs.${tab.tabKey}.contentExpression must be a non-empty string.`
      );
    }

    tab.fields.forEach((field, index) => {
      if (typeof field.fieldId !== "string" || field.fieldId.trim().length === 0) {
        throw new Error(
          `compiledTemplateJson.templatesByEntityType.${frameworkEntityTypeKey}.tabs.${tab.tabKey}.fields[${index}].fieldId must be a non-empty string.`
        );
      }
      if (typeof field.label !== "string" || field.label.trim().length === 0) {
        throw new Error(
          `compiledTemplateJson.templatesByEntityType.${frameworkEntityTypeKey}.tabs.${tab.tabKey}.fields[${index}].label must be a non-empty string.`
        );
      }
    });
  }
}
